import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { FilterTwoIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getOption } from '../../../api/options/get-option'
import { updateOption } from '../../../api/options/update-option'
import DateChip from '../../../components/elements/date-chip'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import JourneyPlaceOptionForm from '../../../components/pages/journey-places/option/journey-place-option-form'
import { Paths } from '../../../lib/constants/path'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import OptionPrices from './option-prices/option-prices'

const JourneyPlaceOptionUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  if (!id || isNaN(parseInt(id))) return <ItemNotFound title="Identifiant d'option invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateOption,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['option', id] })
      toast.success('Option modifiée avec succès')
    }
  })

  const { data, isLoading } = useQuery({
    queryKey: ['option', id],
    queryFn: () => getOption(+id)
  })

  if (isLoading) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        title={'Modifier une option'}
        icon={(props) => <FilterTwoIcon {...props} fill={tailwindColors.secondary.DEFAULT} />}
      />
      <div className={'h-4'} />

      <Box>
        <div className="flex items-center gap-3 mb-4">
          <DateChip date={data.createdAt} label="Créé" />
          <DateChip date={data.updatedAt} label="Mise à jour" />
        </div>
        <JourneyPlaceOptionForm
          defaultValues={{
            code: data.code,
            picto: data.picto,
            picture: data.picture,
            translations: data.translations ?? []
          }}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.JOURNEY_PLACE_OPTIONS_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: data.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: data.id, ...values })
              navigate(Paths.JOURNEY_PLACE_OPTION_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: data.id, ...values })
              back(Paths.JOURNEY_PLACE_OPTIONS_LIST_PAGE)
            }
          })}
        />
      </Box>

      <div className="h-4" />

      <OptionPrices id={+id} />
    </div>
  )
}

export default JourneyPlaceOptionUpdatePage
